/**
 * The one boundary where a row stops being untyped.
 *
 * `RowData` is an index signature -- the backend synthesises a single example row
 * serializer describing a fictional table, so the generated types describe no
 * real one. Everything above this reads cells through here, with the field
 * choosing how its value is understood, and never indexes `field_123` itself.
 */
import { fieldKey, type Field, type FieldTypeName, type RowData } from '@/api/types'
import { behaviorOf } from './behavior'
import type { FieldContext } from './behavior/behavior'
import { readCell } from './compare'

/** A cell's raw value, with a missing one read as null rather than undefined. */
export function cellValue(field: Field, row: RowData): unknown {
  return readCell(row, fieldKey(field.id))
}

/**
 * A value as the text a given field type would show for it.
 *
 * The type is named separately from the field because a formula's is not its
 * own: an array formula's entries are rendered as whatever
 * `array_formula_type` says they are, while the configuration that decides
 * *how* -- a date's format, a number's decimal places -- is on the formula
 * field itself. So the two are passed apart.
 */
export function valueText(
  type: FieldTypeName,
  field: Field,
  value: unknown,
  context?: FieldContext,
): string {
  return behaviorOf(type).toHumanReadable(field, value, context)
}

/**
 * A cell as the text a reader would see.
 *
 * The same rendering the `contains` family of filters matches against, which is
 * the point: a filter that agreed with the stored value and disagreed with what
 * is on screen would be a filter nobody could reason about.
 *
 * Read-only cells everywhere is the grid's whole design -- one portaled editor
 * over the active cell rather than 3,600 mounted inputs -- so this is what a
 * cell renders until a type has a presentation of its own.
 */
export function cellText(field: Field, row: RowData, context?: FieldContext): string {
  return valueText(field.type, field, cellValue(field, row), context)
}
